import { concat, Observable } from "rxjs";
import { map, filter, scan, throttleTime, skipWhile } from "rxjs/operators";
import { ProcessLine, MLCOverviewData, MLCTrendsData, TrendsSingleParamData, WarningIndexMessage, ProcessType } from "../../types";
import { shareSubjectWithDelay } from "../apiHelper";
import { withLogger } from "../LogerServices";
import {
    ignoreTimestamp,
    INTBFilter,
    lineFilter,
    MLCOverviewFilter,
    TBCFFilter,
    groupParamsBasedOnIndex,
    reduceToMaxFourElement,
    MLCTrendsFilter,
    groupOneParam,
} from "./MLCHelper";
import { getMLCINTBOverviewData, getMLCTBCFOverviewData } from "./MLCInitialServices";
import { getMLCINTBWarningIndexDataObserver, getMLCObserver, getMLCTBCFWarningIndexDataObserver } from "./MLCModel";

// OVERVIEW
const getMLCOverviewObservable = (
    processFiltering: (data: MLCOverviewData) => boolean,
    lineFiltering: (data?: MLCOverviewData) => boolean = () => true): Observable<MLCOverviewData> =>
    shareSubjectWithDelay(getMLCObserver()).pipe(
        filter(MLCOverviewFilter),
        filter(processFiltering),
        filter(lineFiltering),
        map(ignoreTimestamp),
    );

export const getMLCINTBOverviewDataObservable = (line: ProcessLine): Observable<MLCOverviewData> => concat(
    withLogger(getMLCINTBOverviewData(line), ProcessType.MLC, { isInitial: true, optionalText: 'INTB - OVERVIEW' }),
    withLogger(getMLCOverviewObservable(INTBFilter, lineFilter(line)), ProcessType.MLC, { optionalText: 'INTB - OVERVIEW' }),
);

export const getMLCTBCFOverviewDataObservable = (): Observable<MLCOverviewData> => concat(
    withLogger(getMLCTBCFOverviewData(), ProcessType.MLC, { isInitial: true, optionalText: 'TBCF - OVERVIEW' }),
    withLogger(getMLCOverviewObservable(TBCFFilter), ProcessType.MLC, { optionalText: 'TBCF - OVERVIEW' })
)

// TRENDS
export const getMLCSingleParamTrendsDataObservable = (
    paramName: string,
    line?: ProcessLine
): Observable<TrendsSingleParamData> =>
    shareSubjectWithDelay(getMLCObserver()).pipe(
        filter(MLCTrendsFilter),
        filter(line ? lineFilter(line) : () => true),
        map((data: MLCTrendsData) => groupOneParam(data, paramName)),
        skipWhile(data => !data),
        scan((acc, val) => Object.assign({}, acc, val), {} as TrendsSingleParamData),
        throttleTime(2000)
    );

// MESSAGES
export const getINTBWarningIndexMessagesDataObservable = () => {
    return shareSubjectWithDelay(getMLCINTBWarningIndexDataObserver()).pipe(
        map(data => Object.values(data)),
        map((data: any[]) => data.flatMap(groupParamsBasedOnIndex)),
        map((data: any[]) => data.filter(el => el.Warning_Index > 0).sort((a, b) => b.Warning_Index - a.Warning_Index)),
        map(reduceToMaxFourElement)
    ) as Observable<WarningIndexMessage[]>
};

export const getTBCFWarningIndexMessagesDataObservable = () => {
    return shareSubjectWithDelay(getMLCTBCFWarningIndexDataObserver()).pipe(
        map(groupParamsBasedOnIndex),
        map((data: any[]) => data.filter(el => el.Warning_Index > 0).sort((a, b) => b.Warning_Index - a.Warning_Index)),
        map(reduceToMaxFourElement),
    ) as Observable<WarningIndexMessage[]>
};